import { Classes, Dialog } from "@blueprintjs/core";
import { FieldGroup } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";
import * as Yup from "yup";

import { DashboardApi, SuiviReassignmentDto, SuiviReassignmentDtoFromJSON } from "../../api";
import { FGWalterSelectInput, SmallFormGenerator } from "../../components";
import { useAuth } from "../../contexts";
import { useApiService, useCrudApi, useTl } from "../../hooks";
import { useReferential } from "../../hooks/useReferential";
import { ETLCodes } from "../../locales";

const StyledDialog = styled(Dialog)`
  width: 700px;
  height: auto;
  background-color: white;
`;

export interface ISuiviReassignmentDialogProps {
  idsSuivi: number[];
  dialogOpen: boolean;
  onClose: () => void;
}

export const SuiviReassignmentDialog: React.FunctionComponent<ISuiviReassignmentDialogProps> = ({
  idsSuivi = [],
  dialogOpen,
  onClose
}) => {
  const { t } = useTl();
  const { user } = useAuth();
  const api = useApiService(DashboardApi);

  const { data, loading, saveItem, saving, validationErrors } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: () => {
          return SuiviReassignmentDtoFromJSON({
            idssuivi: idsSuivi
          });
        },
        saveApiFn: async (d: SuiviReassignmentDto) => {
          return api.dashboardReassignSuivi({ SuiviReassignmentDto: d });
        },
        onSaved: () => onClose()
      }),
      [api, idsSuivi, onClose]
    )
  );

  const FormSchema = React.useMemo(
    () =>
      Yup.object().shape({
        idrealisateurSuivi: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
      }),
    [t]
  );

  const [users, usersLoading] = useReferential(a => a.referentialGetUsers());

  return (
    <StyledDialog
      title={t(ETLCodes.ReassignationSuivi, { nb: idsSuivi.length })}
      isOpen={dialogOpen}
      onClose={() => onClose()}
    >
      <div className={Classes.DIALOG_BODY}>
        <SmallFormGenerator
          initialValues={data}
          onSubmit={saveItem}
          editMode={true}
          validationSchema={FormSchema}
          onCancel={() => onClose()}
          showDeleteButton={false}
          saving={saving}
          loading={loading}
          validationErrors={validationErrors}
        >
          <FieldGroup>
            <FGWalterSelectInput
              name="idrealisateurSuivi"
              label={t(ETLCodes.Remplacant)}
              items={users}
              loading={usersLoading}
            />
          </FieldGroup>
        </SmallFormGenerator>
      </div>
    </StyledDialog>
  );
};
